import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SignaturePad from 'signature_pad';
import { useStore } from '../store';
import apiService from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { Document } from '../types/document';
import { Booking } from '../types/booking';

const BookingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const selectedBooking = useStore((state) => state.selectedBooking);
  const setSelectedBooking = useStore((state) => state.setSelectedBooking);
  
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'evidence' | 'sign'>('details');
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [signedDocumentImage, setSignedDocumentImage] = useState<string | null>(null);
  const { showToast } = useToast();
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);

  useEffect(() => {
    if (id) {
      fetchBookingDetails(parseInt(id));
      fetchDocuments(parseInt(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (canvasRef.current && activeTab === 'sign' && !signedDocumentImage) {
      signaturePadRef.current = new SignaturePad(canvasRef.current, {
        backgroundColor: 'rgb(255, 255, 255)',
        penColor: 'rgb(0, 0, 0)',
      });
      
      // Resize canvas
      const resizeCanvas = () => {
        const canvas = canvasRef.current;
        if (canvas) {
          const ratio = Math.max(window.devicePixelRatio || 1, 1);
          canvas.width = canvas.offsetWidth * ratio;
          canvas.height = canvas.offsetHeight * ratio;
          canvas.getContext('2d')?.scale(ratio, ratio);
          signaturePadRef.current?.clear();
        }
      };
      
      resizeCanvas();
      window.addEventListener('resize', resizeCanvas);
      
      return () => {
        window.removeEventListener('resize', resizeCanvas);
      };
    }
  }, [activeTab, signedDocumentImage]);

  const fetchBookingDetails = async (bookingId: number) => {
    setIsLoading(true);
    try {
      const data = await apiService.getBooking(bookingId);
      setSelectedBooking(data);
    } catch (error) {
      // Error handled by API service
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDocuments = async (bookingId: number) => {
    try {
      const docs = await apiService.getDocuments(bookingId);
      setDocuments(docs);
    } catch (error) {
      // Error handled by API service
    }
  };

  const handleImageCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedImage(file);
    }
  };

  const handleImageUpload = async () => {
    if (!uploadedImage || !id) return;
    
    setIsUploading(true);
    try {
      await apiService.uploadDocument(uploadedImage, parseInt(id));
      showToast('Evidence uploaded successfully!', 'success');
      setUploadedImage(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      // Refresh documents list
      fetchDocuments(parseInt(id));
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to upload evidence. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSignatureClear = () => {
    signaturePadRef.current?.clear();
  };

  const handleSignatureSubmit = async () => {
    if (!signaturePadRef.current || signaturePadRef.current.isEmpty() || !id) {
      showToast('Please provide a signature', 'warning');
      return;
    }
    
    const signatureData = signaturePadRef.current.toDataURL();
    
    setIsUploading(true);
    try {
      const response = await apiService.signDocument(signatureData, parseInt(id));
      
      // Store the signed document image
      if (response.signed_document_image) {
        setSignedDocumentImage(response.signed_document_image);
        showToast('Document signed successfully!', 'success');
      }
      
      // Refresh documents list
      fetchDocuments(parseInt(id));
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to sign document. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (state: string) => {
    const statusMap: Record<string, string> = {
      draft: 'badge-warning',
      confirmed: 'badge-info',
      completed: 'badge-success',
      cancelled: 'badge-error',
    };
    return statusMap[state] || 'badge-ghost';
  };


  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  if (!selectedBooking) {
    return (
      <div className="text-center py-8">
        <p className="text-base-content/60">Booking not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold">{selectedBooking.subject || selectedBooking.name}</h1>
      
      {/* Tabs */}
      <div className="tabs tabs-boxed">
        <button
          className={`tab ${activeTab === 'details' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('details')}
        >
          Details
        </button>
        <button
          className={`tab ${activeTab === 'evidence' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('evidence')}
        >
          Evidence
        </button>
        <button
          className={`tab ${activeTab === 'sign' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('sign')}
        >
          Sign
        </button>
      </div>

      {/* Tab Content */}
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body">
          {activeTab === 'details' && (
            <div className="space-y-4">
              {selectedBooking.start_datetime && (
                <div>
                  <h3 className="text-sm font-semibold text-base-content/60">Date & Time</h3>
                  <p>{formatDate(selectedBooking.start_datetime)}</p>
                  {selectedBooking.end_datetime && (
                    <p className="text-sm text-base-content/60">
                      to {formatDate(selectedBooking.end_datetime)}
                    </p>
                  )}
                </div>
              )}
              
              {selectedBooking.location && (
                <div>
                  <h3 className="text-sm font-semibold text-base-content/60">Location</h3>
                  <p>{selectedBooking.location}</p>
                </div>
              )}
              
              {selectedBooking.user_id && (
                <div>
                  <h3 className="text-sm font-semibold text-base-content/60">Assigned To</h3>
                  <p>{selectedBooking.user_id[1]}</p>
                </div>
              )}
                    
                    {selectedBooking.partner_id && (
                      <div>
                        <h3 className="text-sm font-semibold text-base-content/60">Customer</h3>
                        <p>{selectedBooking.partner_id[1]}</p>
                      </div>
                    )}
                    
                    {selectedBooking.external_attendee_name && (
                      <div>
                        <h3 className="text-sm font-semibold text-base-content/60">External Attendee</h3>
                        <p>{selectedBooking.external_attendee_name}</p>
                        {selectedBooking.external_attendee_email && (
                          <p className="text-sm text-base-content/60">{selectedBooking.external_attendee_email}</p>
                        )}
                        {selectedBooking.external_attendee_phone && (
                          <p className="text-sm text-base-content/60">{selectedBooking.external_attendee_phone}</p>
                        )}
                      </div>
                    )}
                    
                    <div>
                      <h3 className="text-sm font-semibold text-base-content/60">Status</h3>
                      <div className={`badge ${getStatusBadge(selectedBooking.state)}`}>{selectedBooking.state}</div>
                    </div>
                    
                    {selectedBooking.notes && (
                      <div>
                        <h3 className="text-sm font-semibold text-base-content/60">Notes</h3>
                        <p className="whitespace-pre-wrap">{selectedBooking.notes}</p>
                      </div>
                    )}
                    
              <button
                onClick={() => navigate('/payment')}
                className="btn btn-primary w-full mt-4"
              >
                Proceed to Payment
              </button>
            </div>
          )}
          
          {activeTab === 'evidence' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-2">Upload Evidence</h3>
                <p className="text-sm text-base-content/60 mb-4">
                  Take a photo or select an image as evidence for this booking.
                </p>
              </div>
              
              <div className="form-control">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleImageCapture}
                  className="file-input file-input-bordered w-full"
                />
              </div>
              
              {uploadedImage && (
                <div className="space-y-2">
                  <p className="text-sm">Selected: {uploadedImage.name}</p>
                  <img
                    src={URL.createObjectURL(uploadedImage)}
                    alt="Preview"
                    className="max-w-full h-48 object-contain rounded-lg"
                  />
                  <button
                    onClick={handleImageUpload}
                    disabled={isUploading}
                    className={`btn btn-primary w-full ${isUploading ? 'loading' : ''}`}
                  >
                    {isUploading ? 'Uploading...' : 'Upload Evidence'}
                  </button>
                </div>
              )}
              
              {/* Documents List */}
              {documents.length > 0 && (
                <div>
                  <h3 className="text-md font-semibold mb-2">Uploaded Documents</h3>
                  <div className="space-y-2">
                    {documents.map((doc) => {
                      const isImage = doc.file_type?.startsWith('image/');
                      
                      return (
                        <div key={doc.id} className="flex gap-3 p-3 bg-base-200 rounded-lg">
                          {/* Image preview on the left */}
                          <div className="flex-shrink-0">
                            {isImage && doc.document_url ? (
                              <div className="w-24 h-24 rounded-lg overflow-hidden bg-base-300">
                                <img
                                  src={doc.document_url}
                                  alt={doc.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    // If image fails to load, show icon instead
                                    const parent = e.currentTarget.parentElement;
                                    if (parent) {
                                      parent.innerHTML = `
                                        <div class="w-full h-full flex items-center justify-center bg-primary/10">
                                          <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                          </svg>
                                        </div>
                                      `;
                                    }
                                  }}
                                />
                              </div>
                            ) : doc.file_type?.includes('pdf') ? (
                              <div className="w-24 h-24 bg-error/10 rounded-lg flex items-center justify-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                              </div>
                            ) : (
                              <div className="w-24 h-24 bg-base-300 rounded-lg flex items-center justify-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-base-content/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </div>
                            )}
                          </div>
                          
                          {/* Document info in the middle */}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{doc.name}</p>
                            <p className="text-sm text-base-content/60">
                              {new Date(doc.create_date).toLocaleDateString()}
                            </p>
                            {doc.is_signed && (
                              <p className="text-xs text-success mt-1">
                                ✓ Signed
                              </p>
                            )}
                          </div>
                          
                          {/* Download button on the right */}
                          {doc.document_url && (
                            <a
                              href={doc.document_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-ghost flex-shrink-0"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'sign' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-2">Sign Document</h3>
                <p className="text-sm text-base-content/60 mb-4">
                  {signedDocumentImage ? 'Document signed successfully!' : 'Please sign below to confirm the booking.'}
                </p>
              </div>
              
              {signedDocumentImage ? (
                <div className="space-y-4">
                  <div className="border-2 border-base-300 rounded-lg overflow-hidden">
                    <img
                      src={`data:image/png;base64,${signedDocumentImage}`}
                      alt="Signed Document"
                      className="w-full"
                    />
                  </div>
                  
                  <button
                    onClick={() => {
                      setSignedDocumentImage(null);
                    }}
                    className="btn btn-outline w-full"
                  >
                    Sign Again
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="border-2 border-base-300 rounded-lg">
                    <canvas
                      ref={canvasRef}
                      className="w-full h-48 touch-none"
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={handleSignatureClear}
                      className="btn btn-outline flex-1"
                    >
                      Clear
                    </button>
                    <button
                      onClick={handleSignatureSubmit}
                      disabled={isUploading}
                      className={`btn btn-primary flex-1 ${isUploading ? 'loading' : ''}`}
                    >
                      {isUploading ? 'Submitting...' : 'Submit Signature'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingDetail;